import { FC, useCallback, useState } from 'react';
import Page, { PageProps } from '@/components/page';
import DataTable from '@/components/data-table';
import { DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import { Box, Stack } from '@mui/material';
import { QueryKey } from '@/lib/query-client';
import Button from '@/components/button';
import PageHeader from '@/components/page/header';
import Tooltip from '@/components/tooltip';
import DomSize from '@/components/dom-size';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { useQuery } from 'react-query';
import useQueryHelper from '@/hooks/use-query-helper';
import { getClassifiers, getClassifierUsages } from '@/lib/services/classification.service';
import { ClassifierSubType } from '@/lib/models/classifier';
import Text from '@/components/text';
import ControlChip from '@/components/chip/control-chip';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import IntercomButton from '@/components/intercom/intercom-button';
import useDrawer from '@/hooks/use-drawer.hook';
import NoneChip from '@/components/chip/none-chip';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 2, sortable: true },
  { name: 'type', label: 'Type', flex: 0.75, sortable: false },
  { name: 'status', label: 'Status' },
  { name: 'defaultPolicy', label: 'Default Policy' },
  { name: 'groupPolicies', label: 'Group Policies' },
  { name: 'action', label: '', flex: 0.5, sortable: false },
];

const ClassifiersPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');
  const {
    canChangeClassifiers,
    hasClassifiers,
    getTooltip,
    loading: featureLoading,
  } = useFeature();

  const usageQuery = useQuery([QueryKey.ClassifierUsage], async () => getClassifierUsages());
  const { data: classifierUsageMap } = usageQuery;
  const { showLoader: usageLoading } = useQueryHelper(usageQuery);

  const handleView = useCallback(
    (classifierId?: string) => {
      openDrawer('classifier', {
        id: classifierId || null,
      });
      setSelectedRowId(classifierId || '');
    },
    [openDrawer]
  );

  const loadData = useCallback(
    async (pageParams: DtPage, filter?: DtFilter) => {
      const { page, pageSize, sort } = pageParams;
      const dataset = await getClassifiers(page, pageSize, sort, filter);
      return {
        ...dataset,
        rows: dataset.rows.map((model) => {
          const { id, name, description, enabled, subtype } = model;
          const hasDescription = Boolean(description?.trim());
          const status = enabled ? 'enabled' : 'disabled';
          const { policies } = classifierUsageMap?.get(id) || {
            policies: [],
            inUse: false,
          };

          const isRegex = subtype === ClassifierSubType.Regex;
          const orgPolicy = policies.find(({ scope }) => scope === 'org');
          const orgEntities = isRegex
            ? orgPolicy?.regexPatterns?.entities
            : orgPolicy?.classifierControls.entities;
          const defaultPolicyAction =
            orgEntities?.find(({ classifierId }) => classifierId === id)?.action || 'ignore';
          const groupPolicies = policies.filter(({ scope }) => scope === 'group');

          const hasPolicies = !!groupPolicies.length;
          const usageText =
            groupPolicies.length === 1 ? '1 Policy' : `${groupPolicies.length} Policies`;

          return {
            id,
            name: (
              <Stack>
                <Text size="small">{name}</Text>
                {hasDescription && (
                  <Text size="small" color="grey" dotdot>
                    {description}
                  </Text>
                )}
              </Stack>
            ),
            type: <Text size="small">{isRegex ? 'Entity' : 'Intent'}</Text>,
            status: <StatusChip value={status as ChipStatus} size="small" />,
            defaultPolicy: <ControlChip size="small" value={defaultPolicyAction} />,
            groupPolicies: hasPolicies ? usageText : <NoneChip />,
            action: <OpenDrawerButton onClick={() => handleView(id)} />,
          };
        }),
      };
    },
    [classifierUsageMap, handleView]
  );

  if (featureLoading || usageLoading) {
    return null;
  }

  const title = hasClassifiers ? 'Classifiers' : 'Intent Classifiers';

  return (
    <Page title={title}>
      <PageHeader>
        <PageBlurb>
          <Stack gap={2}>
            <Box>
              Define classifiers to perform matching on AI prompts. Then apply policies based on one
              or more classifiers. Use intent topics to perform semantic classification of AI
              prompts.
              {hasClassifiers &&
                ' Entity classifiers are used to perform pattern matching on AI prompts.'}
            </Box>
            <Box>
              <IntercomButton
                intercomArticle="intent-topics"
                label={`Learn About ${title}`}
                flatButton
              />
            </Box>
          </Stack>
        </PageBlurb>

        <PageTools>
          <Tooltip title={getTooltip('add-classifier')} disabled={canChangeClassifiers}>
            <Button
              label="Add Classifier"
              icon="plus"
              onClick={() => handleView()}
              disabled={!canChangeClassifiers}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="change-classifier" can={canChangeClassifiers} />
      <DomSize>
        <DataTable
          queryKey={QueryKey.ClassifiersDataset}
          columns={columns}
          onLoad={loadData}
          rowHeight={70}
          search
          selectedRowId={selectedRowId}
          poll={15000}
        />
      </DomSize>
    </Page>
  );
};

export default ClassifiersPage;
